// Session bookkeeping for the CUSTOMER portal (ADR-179 P2). Deliberately a
// SEPARATE module from src/lib/auth/session.ts (the operator's), not a
// shared/parameterized one — ADR-179 D4's whole rationale for a separate
// surface is that the customer path must have NO code path that can reach
// operator state or vice versa. Mirrors the operator module's cookie-mode
// shape closely (same HttpOnly-cookie / in-memory-CSRF / cookie-fallback
// discipline) because the backend substrate is deliberately byte-for-byte
// parallel (services/console/server/session_customer.go's package doc:
// "Parallels session.go's operator password-login file closely").
//
// Unlike the operator surface, the customer portal has NO API-key mode —
// P1 ships exactly three session-minting paths (password, OIDC, SAML), all
// of which mint the SAME customer_sessions row shape via mintCustomerSession
// and land here as a cookie session. There is no bearer-key equivalent.

// customerCsrfCookieName mirrors services/console/server/session_customer.go's
// customerCSRFCookieName constant EXACTLY ("customer_csrf") — distinct from
// the operator's "console_csrf" so the two cookies can never be confused.
const CUSTOMER_CSRF_COOKIE_NAME = 'customer_csrf'

let customerSessionCsrf: string | null = null

// setCustomerSessionCsrf records the CSRF token minted by a successful
// /customer/auth/login call (or recovered from the customer_csrf cookie on
// reload). In-memory only, by design — see the operator session.ts module
// for the full rationale; it applies identically here.
export function setCustomerSessionCsrf(token: string): void {
  customerSessionCsrf = token
}

// getCustomerSessionCsrf returns the in-memory CSRF token, falling back to
// the non-HttpOnly customer_csrf cookie the server set alongside the
// customer_session cookie on login (password OR an SSO callback redirect —
// mintCustomerSession is the single shared minting point for all three
// paths, so this cookie is set identically regardless of which auth path
// produced the session).
export function getCustomerSessionCsrf(): string | null {
  if (customerSessionCsrf) return customerSessionCsrf
  const fromCookie = readCustomerCsrfCookie()
  if (fromCookie) customerSessionCsrf = fromCookie
  return customerSessionCsrf
}

// clearCustomerSessionCsrf drops the in-memory CSRF token (logout, or any
// auth failure). The HttpOnly customer_session cookie and the customer_csrf
// cookie are cleared server-side by /customer/auth/logout (and defensively
// on any 401); this only clears the SPA's own in-memory mirror.
export function clearCustomerSessionCsrf(): void {
  customerSessionCsrf = null
}

function readCustomerCsrfCookie(): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    const name = part.slice(0, eq).trim()
    if (name === CUSTOMER_CSRF_COOKIE_NAME) {
      return decodeURIComponent(part.slice(eq + 1).trim()) || null
    }
  }
  return null
}

// hasStoredCustomerSession reports whether the SPA has a locally-detectable
// customer session to probe — i.e. a recoverable CSRF token (cookie mode is
// the ONLY mode on the customer surface). The portal router's coarse
// client-side gate and CustomerAuthProvider's initial probe use this to
// decide whether to attempt WhoAmI at all; real enforcement is server-side
// (the interceptor's fifth branch + FORCE-RLS, per ADR-179 D2.3).
export function hasStoredCustomerSession(): boolean {
  return getCustomerSessionCsrf() !== null
}

// clearCustomerSession wipes the LOCAL trace of the customer session (the
// in-memory CSRF token) so the SPA falls back to the portal login screen.
// It does NOT itself hit /customer/auth/logout (that is logout()'s job, to
// revoke the server-side session row and clear the HttpOnly cookie); this
// is the purely client-side teardown used both by an explicit logout and by
// the 401 choke point when the server has already invalidated the session.
export function clearCustomerSession(): void {
  clearCustomerSessionCsrf()
}
